/**
 * GeoGebra 画布 —— SPEC.md §3 / §4A.6
 *
 * 通过官方 deployggb.js 注入 applet；加载完成后把 GGBAppletApi 写入 store。
 * 监听 ggbAppName 变化：classic → 3d 时销毁旧 applet 并重建。
 */
import { useEffect, useRef } from "react";
import { useAppStore } from "../store/useAppStore";
import type { GGBAppletApi } from "../types/ggb";

const CONTAINER_ID = "ggb-container";

export function GGBCanvas() {
  const setGGBApi = useAppStore(s => s.setGGBApi);
  const ggbAppName = useAppStore(s => s.ggbAppName);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const injectingRef = useRef<boolean>(false);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const inject = (w: number, h: number) => {
      if (!window.GGBApplet) {
        setTimeout(() => inject(w, h), 100);
        return;
      }
      if (injectingRef.current) return;
      injectingRef.current = true;

      // ★ 销毁旧 applet
      containerEl.innerHTML = "";

      console.log("[AiGGB] injecting applet:", ggbAppName, w, "×", h);

      const applet = new window.GGBApplet(
        {
          appName: ggbAppName,              // ★ "classic" 或 "3d"
          width: w,
          height: h,
          showToolBar: true,
          showAlgebraInput: true,
          showMenuBar: false,
          showResetIcon: false,
          showKeyboardOnFocus: false,
          enableLabelDrags: false,
          enableShiftDragZoom: true,
          enableRightClick: false,
          errorDialogsActive: false,
          useBrowserForJS: true,
          language: "zh",
          preventFocus: true,
          appletOnLoad: (api: GGBAppletApi) => {
            setGGBApi(api);
            injectingRef.current = false;
            const apiAny = api as unknown as Record<string, unknown>;
            console.log("[AiGGB] " + ggbAppName + " loaded, enable3D:", typeof apiAny.enable3D);
          }
        },
        true
      );
      // 3D app 需要 web3d 代码库
      if (ggbAppName === "3d" && typeof (applet as unknown as { setHTML5Codebase?: (url: string) => void }).setHTML5Codebase === "function") {
        (applet as unknown as { setHTML5Codebase: (url: string) => void }).setHTML5Codebase("https://www.geogebra.org/apps/latest/web3d/");
      }
      applet.inject(CONTAINER_ID);
    };

    // 初始注入或 appName 变化时重建
    const rect = containerEl.getBoundingClientRect();
    lastSizeRef.current = { w: Math.floor(rect.width), h: Math.floor(rect.height) };
    inject(lastSizeRef.current.w, lastSizeRef.current.h);

    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        const h = Math.floor(entry.contentRect.height);
        const last = lastSizeRef.current;
        if (Math.abs(w - last.w) < 40 && Math.abs(h - last.h) < 40) continue;
        lastSizeRef.current = { w, h };
        scheduleRebuild(() => inject(w, h));
      }
    });
    ro.observe(containerEl);

    return () => {
      ro.disconnect();
      setGGBApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ggbAppName]); // ★ ggbAppName 变化时销毁重建

  return (
    <section className="ggb-panel">
      <div className="ggb-host" id={CONTAINER_ID} ref={containerRef} />
    </section>
  );
}

let rebuildTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleRebuild(fn: () => void) {
  if (rebuildTimer !== null) clearTimeout(rebuildTimer);
  rebuildTimer = setTimeout(() => {
    rebuildTimer = null;
    fn();
  }, 350);
}
